/**
 * builderRoomControls — pure derivation for the room's multi-builder control surface.
 *
 * The generative fan-out can pause SEVERAL builders for input at once and run several more. The
 * room's control model (locked with the user):
 *   - RUNNING builders → a Stop control ON their own roster row (targeted by runId). No above-prompt
 *     element for Stop — the user picks the right row.
 *   - AWAITING builders → a passive "N awaiting your response" notice above the prompt + an
 *     "awaiting" affordance on each row. The user clicks ONE awaiting row → that builder's
 *     interaction band opens above the prompt (one at a time), bound to that specific wait.
 *
 * This module turns the reducer's ``pendingBuilderWaits`` + ``agentRoster`` (plus the locally-
 * handled-wait set and the current selection) into the flags/objects the room renders. Pure +
 * defensive so it unit-tests without React.
 */

/**
 * @param {Object} args
 * @param {Array<Object>} [args.pendingBuilderWaits]  Reducer output — ALL unresolved waits
 *   ({agentId, runId, waitEventId, prompt, interaction}).
 * @param {Array<Object>} [args.agentRoster]  Reducer output — the run-level roster rows.
 * @param {Array<string>|Set<string>} [args.handledWaitEventIds]  Waits the local user already
 *   answered/cancelled this session (dismissed optimistically before the RESOLVED echo).
 * @param {string|null} [args.selectedWaitRunId]  The runId of the wait the user clicked (band target).
 * @returns {{
 *   awaitingWaits: Array<Object>,
 *   awaitingCount: number,
 *   selectedWait: Object|null,
 *   runningBuilderRunIds: Object<string,string>,
 * }}
 */
export function deriveBuilderControls({
  pendingBuilderWaits = [],
  agentRoster = [],
  handledWaitEventIds = [],
  selectedWaitRunId = null,
} = {}) {
  const handled = handledWaitEventIds instanceof Set
    ? handledWaitEventIds
    : new Set(Array.isArray(handledWaitEventIds) ? handledWaitEventIds : []);

  const waits = Array.isArray(pendingBuilderWaits) ? pendingBuilderWaits : [];
  // A wait is actionable when it has a runId and the local user hasn't already handled it. Enrich
  // each with the roster label so the notice/list can name the agent without a second lookup.
  const roster = Array.isArray(agentRoster) ? agentRoster : [];
  const rosterById = new Map(roster.map((r) => [String(r.agentId), r]));
  const awaitingWaits = waits
    .filter((w) => w && w.runId && !(w.waitEventId && handled.has(w.waitEventId)))
    .map((w) => {
      const row = w.agentId ? rosterById.get(String(w.agentId)) : null;
      return { ...w, label: (row && (row.label || row.agentId)) || w.agentId || '' };
    });

  // The band target: the awaiting wait the user selected (by runId). No auto-pick — the user
  // handles one at a time, so nothing opens until they click a row.
  const selectedWait = selectedWaitRunId
    ? (awaitingWaits.find((w) => w.runId === selectedWaitRunId) || null)
    : null;

  // Per-agent runId for RUNNING builders → the Stop control on their row targets this.
  const runningBuilderRunIds = {};
  for (const r of roster) {
    if (r && r.isBuilder && r.status === 'running' && r.runId) {
      runningBuilderRunIds[String(r.agentId)] = String(r.runId);
    }
  }

  return {
    awaitingWaits,
    awaitingCount: awaitingWaits.length,
    selectedWait,
    runningBuilderRunIds,
  };
}

/** Look up the pending wait for a clicked roster row (by agentId, falling back to runId). Returns
 *  the wait's runId so the caller can set it as the selection, or null when the row isn't awaiting. */
export function waitRunIdForAgent(pendingBuilderWaits, agentId) {
  const waits = Array.isArray(pendingBuilderWaits) ? pendingBuilderWaits : [];
  const id = String(agentId || '').trim();
  if (!id) return null;
  const match = waits.find((w) => w && (String(w.agentId || '') === id || String(w.runId || '') === id));
  return match ? (match.runId || null) : null;
}
